import PropTypes from "prop-types"
import { Button } from "antd"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { toaster } from "components/common/Toast"
import { useNewConfirmModal } from "modules/modals/hooks/useNewConfirmModal"
import { chargePowerTranzSavedCard } from "store/payment-provider"
import { getPowerTranzErrorCode, getPowerTranzErrorMessage, isPendingAttemptState } from "helpers/powertranz"
import { SEND_LINK_ERROR_CODES } from "constants/powertranz"

// Charges a pending PowerTranz income (balance or price adjustment) to the card the client saved
// with consent at checkout — a guest-not-present token sale on the guide's merchant account.
const ChargeSavedCardButton = ({ bookingId, incomeId, savedCard, onCharged }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { showConfirm, modal } = useNewConfirmModal()
  const isCharging = useSelector((state) => state.paymentProvider.chargePowerTranzSavedCard.loading)

  const handleChargeConfirm = async () => {
    try {
      const response = await dispatch(chargePowerTranzSavedCard({ booking_id: bookingId, ...(incomeId && { income_id: incomeId }) }))
      // 202 { state: "processing" }: the outcome is still being confirmed — never charge again.
      if (isPendingAttemptState(response?.state)) toaster.info(t("powertranz.saved_card.charge_processing"))
      else toaster.success(t("powertranz.saved_card.charge_success", { lastFour: savedCard.lastFour }))
    } catch (error) {
      const code = getPowerTranzErrorCode(error)
      const message = getPowerTranzErrorMessage(t, code)
      toaster.error(SEND_LINK_ERROR_CODES.includes(code) ? `${message} ${t("powertranz.saved_card.send_link_instead")}` : message)
    } finally {
      onCharged?.()
    }
  }

  const handleChargeRequest = () => {
    showConfirm(
      {
        message: t("powertranz.saved_card.charge_confirmation", { brand: savedCard.brand, lastFour: savedCard.lastFour }),
        confirmLabel: t("powertranz.saved_card.charge"),
        cancelLabel: t("global.cancel")
      },
      handleChargeConfirm
    )
  }

  return (
    <>
      <Button size="small" onClick={handleChargeRequest} loading={isCharging} disabled={isCharging}>
        {t("powertranz.saved_card.charge_button", { lastFour: savedCard.lastFour })}
      </Button>
      {modal}
    </>
  )
}

ChargeSavedCardButton.propTypes = {
  bookingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  // A specific open balance link; without it the API charges the booking's pending balance.
  incomeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  savedCard: PropTypes.shape({
    brand: PropTypes.string,
    lastFour: PropTypes.string
  }).isRequired,
  onCharged: PropTypes.func
}

export default ChargeSavedCardButton
